/**
 * Programmatic CCPA opt-out / opt-in.
 *
 *   privacy.optOut()   → ccpa_opt_out=1, refresh __uspapi stub to '1YY-',
 *                        flip consent to 'denied'
 *   privacy.optIn()    → ccpa_opt_out=0, refresh stub to '1YN-',
 *                        flip consent to 'granted'
 *   privacy.status()   → 'opted-in' | 'opted-out' from cookie
 *
 * The SDK only installs `__uspapi` when:
 *   - the build is not sealed — a sealed bundle is a guest on a page we do not
 *     own, so it reads that page's privacy signals and never publishes its own,
 *   - no upstream `__uspapi` is already on the page, AND
 *   - the site is in a CCPA-style jurisdiction (`cmp.ccpa_applicable: true`).
 *
 * No DOM is rendered — the host owns any "Do Not Sell" link UX.
 */

import { getCookie, setCookie } from '../../util/cookies';
import { warn } from '../../util/log';
import type { ConsentEngine } from '../../core/consent/engine';
import type { PrivacyApi, PrivacyStatus } from '../../core/consent/port';
import type { CmpInfo } from '../../core/config/types';
import { buildUspString } from './uspString';
import type { UspApi } from './cmpApis';
import { track } from '../../core/analytics/track';
import { CORE_EVENTS } from '../../core/analytics/coreEvents';

const COOKIE_NAME = 'ccpa_opt_out';
const COOKIE_DAYS = 365;

export interface PrivacyContext {
  rootDomain: string;
  cmp: CmpInfo;
  consent: ConsentEngine;
}

export const COOKIE_OPTOUT_NAME = COOKIE_NAME;

export function createPrivacyApi(ctx: PrivacyContext): PrivacyApi {
  installUspStubIfNeeded(ctx);

  return {
    optOut(): void {
      setCookie(COOKIE_NAME, '1', { days: COOKIE_DAYS, domain: ctx.rootDomain });
      installUspStubIfNeeded(ctx);
      ctx.consent.setResolved('denied');
      track(CORE_EVENTS.ccpaOptOut);
    },
    optIn(): void {
      setCookie(COOKIE_NAME, '0', { days: COOKIE_DAYS, domain: ctx.rootDomain });
      installUspStubIfNeeded(ctx);
      ctx.consent.setResolved('granted');
    },
    status(): PrivacyStatus {
      return isCcpaOptedOut() ? 'opted-out' : 'opted-in';
    },
  };
}

export function isCcpaOptedOut(): boolean {
  return getCookie(COOKIE_NAME) === '1';
}

/** Positive guard, so the stub and its string builder leave sealed bundles. */
function installUspStubIfNeeded(ctx: PrivacyContext): void {
  if (!__SEALED__) installUspStub(ctx);
}

function installUspStub(ctx: PrivacyContext): void {
  if (typeof window === 'undefined') return;

  // Skip if an upstream __uspapi already exists and we didn't install it.
  if (window.__uspapi && (window.__uspapi as { _kz?: boolean })._kz !== true) {
    return;
  }
  // Skip when CCPA doesn't apply — no point in advertising USP signals.
  if (!ctx.cmp.ccpa_applicable) return;

  const optedOut = isCcpaOptedOut();
  const uspString = buildUspString({ ccpaApplicable: true, optedOut });

  const stub: UspApi & { _kz?: boolean } = function (cmd, _version, cb) {
    try {
      if (cmd === 'getUSPData') {
        cb({ uspString }, true);
      } else {
        cb(null, false);
      }
    } catch (err) {
      warn('uspapi stub callback threw', err);
    }
  };
  stub._kz = true;
  window.__uspapi = stub;
}
